// Org-wide canonical-values list editor for the governance metrics
// (statusCount, issueTypeCompliance). Items not in this list show up on the
// Workflow Hygiene cards as "non-canonical". Reordering uses up/down arrow
// buttons rather than drag-and-drop because no DnD library is installed in
// the repo — spec section "Edge Cases & Rules" explicitly accepts arrow
// buttons as the v1 fallback.
//
// Duplicate detection is client-side and non-blocking — the spec allows
// duplicates "with a UI warning, do not enforce uniqueness server-side"
// because the user might be in the middle of an edit and rely on a fresh
// duplicate before removing the old entry.

import { Button } from '@/components/ui';

export interface CanonicalValuesEditorProps {
  values: string[];
  onChange: (next: string[]) => void;
}

function findDuplicates(values: string[]): string[] {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const raw of values) {
    const v = raw.trim();
    if (v.length === 0) continue;
    if (seen.has(v)) duplicates.add(v);
    else seen.add(v);
  }
  return [...duplicates];
}

export function CanonicalValuesEditor({ values, onChange }: Readonly<CanonicalValuesEditorProps>) {
  const duplicates = findDuplicates(values);

  function handleEdit(index: number, next: string) {
    onChange(values.map((v, i) => (i === index ? next : v)));
  }

  function handleRemove(index: number) {
    onChange(values.filter((_, i) => i !== index));
  }

  function handleMove(from: number, to: number) {
    if (to < 0 || to >= values.length) return;
    const next = [...values];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  }

  function handleAdd() {
    onChange([...values, '']);
  }

  return (
    <div className="space-y-2">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Canonical values
        </p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Items not in this list are reported as non-canonical on the Workflow Hygiene cards.
        </p>
      </div>

      {duplicates.length > 0 && (
        <p
          role="alert"
          className="rounded-md border border-warning bg-warning/10 px-2 py-1 text-xs text-warning"
        >
          Duplicate entries: {duplicates.join(', ')}
        </p>
      )}

      {values.length === 0 && (
        <p className="text-xs italic text-muted-foreground">
          No canonical values configured. Cards will render the empty state until you add at least one.
        </p>
      )}

      <ul className="space-y-1.5" aria-label="Canonical values">
        {values.map((value, index) => {
          const label = value || `value ${index + 1}`;
          return (
          <li key={index} className="flex items-center gap-1.5">
            <input
              className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-xs focus:border-primary focus:outline-none"
              value={value}
              onChange={(e) => handleEdit(index, e.target.value)}
              aria-label={`Canonical value ${index + 1}`}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMove(index, index - 1)}
              disabled={index === 0}
              aria-label={`Move ${label} up`}
              className="px-2 text-xs"
            >
              ↑
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleMove(index, index + 1)}
              disabled={index === values.length - 1}
              aria-label={`Move ${label} down`}
              className="px-2 text-xs"
            >
              ↓
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleRemove(index)}
              aria-label={`Remove ${label}`}
              className="text-xs text-muted-foreground hover:text-error"
            >
              Remove
            </Button>
          </li>
          );
        })}
      </ul>

      <Button variant="ghost" size="sm" onClick={handleAdd} className="text-xs">
        + Add value
      </Button>
    </div>
  );
}
